/**
 * Rental Chat Service
 *
 * Connects the frontend chat widget to the RAG-based backend API.
 */

import { resolveApiBaseUrl } from '../utils/apiClient';
import { formatAmount } from '../utils/numberFormat';

const API_BASE_URL = resolveApiBaseUrl();

export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatProperty {
  id: string;
  title: string;
  price: number;
  currency: string;
  city: string;
  bedrooms: number;
  bathrooms: number;
  propertyType: string;
  imageUrl?: string;
  slug?: string;
  relevanceScore?: number;
}

export interface ChatAction {
  type: 'navigate' | 'search' | 'show_properties' | 'none';
  payload?: string | Record<string, unknown>;
}

export interface ChatFilters {
  city?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  isFurnished?: boolean;
  isPetFriendly?: boolean;
}

export interface ChatRequest {
  message: string;
  history?: ChatHistoryMessage[];
  currentUrl?: string;
  sessionId?: string;
  preferredLanguage?: 'en' | 'ar';
  filters?: ChatFilters;
}

export interface ChatResponse {
  success: boolean;
  data?: {
    message: string;
    suggestions?: string[];
    action?: ChatAction;
    properties?: ChatProperty[];
    filters?: ChatFilters;
  };
  error?: string;
  errorAr?: string;
}

// Generate a unique session ID
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

// Get or create session ID
let sessionId: string | null = null;
function getSessionId(): string {
  if (!sessionId) {
    const stored = sessionStorage.getItem('rental_chat_session');
    if (stored) {
      sessionId = stored;
    } else {
      sessionId = generateSessionId();
      sessionStorage.setItem('rental_chat_session', sessionId);
    }
  }
  return sessionId;
}

/**
 * Send a chat message to the RAG-based backend
 */
export async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/rental-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: request.message,
        history: request.history || [],
        currentUrl: request.currentUrl || window.location.pathname,
        sessionId: request.sessionId || getSessionId(),
        preferredLanguage: request.preferredLanguage || 'en',
        filters: request.filters || {},
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP error: ${response.status}`,
        errorAr: errorData.errorAr,
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Chat API error:', error);
    return {
      success: false,
      error: 'Failed to connect to chat service. Please try again.',
      errorAr: 'فشل الاتصال بخدمة المحادثة. يرجى المحاولة مرة أخرى.',
    };
  }
}

/**
 * Get chat configuration status
 */
export async function getChatConfig(): Promise<{
  openaiConfigured: boolean;
  chatModel: string;
  status: 'full' | 'limited';
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/rental-chat/config`);
    if (!response.ok) {
      throw new Error('Failed to get config');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Failed to get chat config:', error);
    return {
      openaiConfigured: false,
      chatModel: 'fallback',
      status: 'limited',
    };
  }
}

/**
 * Format a property card for display in chat
 */
export function formatPropertyCard(property: ChatProperty, lang: 'en' | 'ar'): string {
  const price = formatAmount(property.price) || 'N/A';
  const currency = property.currency || 'SAR';

  if (lang === 'ar') {
    return `🏠 ${property.title}
💰 ${price} ${currency}
📍 ${property.city || 'N/A'}
🛏️ ${property.bedrooms || 'N/A'} غرف | 🚿 ${property.bathrooms || 'N/A'} دورات مياه`;
  }

  return `🏠 ${property.title}
💰 ${price} ${currency}
📍 ${property.city || 'N/A'}
🛏️ ${property.bedrooms || 'N/A'} beds | 🚿 ${property.bathrooms || 'N/A'} baths`;
}

/**
 * Clear the chat session
 */
export function clearChatSession(): void {
  sessionId = null;
  sessionStorage.removeItem('rental_chat_session');
}

export default {
  sendChatMessage,
  getChatConfig,
  formatPropertyCard,
  clearChatSession,
  getSessionId,
};
